import { isDefined, isNullish } from 'remeda';
import { APPLICATION_LOCALES, type ApplicationLocale } from 'types';

function report(message: string): void {
  void import('@sentry/react').then(({ captureMessage }) => captureMessage(message));
}

import {
  type LocaleTree,
  PLURAL_CATEGORIES,
  type PluralCategory,
  type PluralForms,
  type TranslateFunction,
} from './types';

export function getTranslationForKey(
  key: Parameters<TranslateFunction>[0],
  params: Parameters<TranslateFunction>[1],
  localeTree: LocaleTree,
  locale: ApplicationLocale,
): string {
  const node = getNodeForKey(key, localeTree);
  const rawText = isPluralForms(node) ? selectPluralForm(node, params?.count, locale) : node;

  if (isNullish(params)) return rawText;

  return replaceParamsInText(rawText, params);
}

function getNodeForKey(
  key: Parameters<TranslateFunction>[0] | undefined,
  localeTree: LocaleTree,
): string | PluralForms {
  if (!isDefined(key)) {
    report('Key provided to getTranslationForKey was `undefined`');
    return '';
  }

  const fragments = key.toString().split('.');
  let currentElement = localeTree;
  for (const fragment of fragments) currentElement = currentElement[fragment];

  return currentElement;
}

function isPluralForms(node: string | PluralForms): node is PluralForms {
  return (
    typeof node === 'object' &&
    isDefined(node) &&
    Object.keys(node).every(category => PLURAL_CATEGORIES.includes(category as PluralCategory))
  );
}

function selectPluralForm(
  forms: PluralForms,
  count: string | number | undefined,
  locale: ApplicationLocale,
): string {
  if (isNullish(count)) {
    report('A pluralised translation key was used without a `count` param');
    return forms.many ?? '';
  }

  const numericCount = Number(count);
  if (numericCount === 0 && isDefined(forms.zero)) return forms.zero;

  const isSingular = new Intl.PluralRules(locale).select(numericCount) === 'one';

  return (isSingular ? forms.one : forms.many) ?? forms.many ?? '';
}

function replaceParamsInText(
  text: string,
  params: Required<Parameters<TranslateFunction>>[1],
): string {
  let newText = text;

  Object.keys(params).forEach(paramKey => {
    newText = newText.replace(`$\{${paramKey}}`, String(params[paramKey]));
  });

  return newText;
}

export function getPreferredBrowserLanguageOrDefault(): ApplicationLocale {
  const navigatorLanguage = navigator.language;

  let parsedNavigatorLocale = '';
  if (navigatorLanguage.includes('-')) [parsedNavigatorLocale] = navigatorLanguage.split('-');
  if (navigatorLanguage.length === 2) parsedNavigatorLocale = navigatorLanguage;

  if (parsedNavigatorLocale in APPLICATION_LOCALES)
    return parsedNavigatorLocale as ApplicationLocale;

  return 'fr';
}
